// Kitchen Display System Types
// Note: These match the string values from .NET API (enums serialize as strings)

import { ProductionCenterSimple } from './products';

export enum KitchenOrderStatus {
  Pending = 'Pending',
  Preparing = 'Preparing',
  Ready = 'Ready',
  Completed = 'Completed',
}

export interface KdsOrderItemDto {
  id: string;
  orderItemId?: string;
  productName: string;
  quantity: number;
  variants?: string;
  notes?: string;
  seatNumber?: number | null;
  isCancelled?: boolean;
  isCompleted?: boolean;
  status?: KitchenOrderStatus;
  /** Sort order within the KitchenOrder (backend always sends, defaults to 0). */
  sortOrder: number;
  /** True when this item is an aggregated daily-menu course row. */
  isDailyMenuCourse?: boolean;
  productionCenters?: ProductionCenterSimple[];
}

/**
 * Renglón vivo de una orden que ninguna comanda cubre.
 *
 * Debería venir siempre vacío ahora que el backend crea la comanda dentro de la misma
 * transacción que guarda los renglones. Existe como red de seguridad: "Reenviar Comanda"
 * solo sabe leer de KitchenOrders, así que sin esto un producto cuya comanda nunca se creó
 * es irrecuperable desde la UI — el mesero ve la lista incompleta y ninguna señal de que
 * falta algo.
 */
export interface OrderItemWithoutComandaDto {
  orderItemId: string;
  productName: string;
  quantity: number;
  variants?: string;
  notes?: string;
}

export interface KitchenOrderDto {
  id: string;
  orderId: string;
  orderNumber: number;
  tableName: string;
  tableNumber: string;
  areaName: string;
  waiterName: string;
  waiterId?: string;
  sentAt: string;
  startedAt?: string;
  readyAt?: string;
  completedAt?: string;
  /**
   * For daily-menu orders: advances every time a course is dispatched so the KDS timer
   * resets per course. Frontend should use `phaseStartedAt ?? sentAt` for the tick.
   */
  phaseStartedAt?: string;
  elapsedSeconds: number;
  status: KitchenOrderStatus;
  filteredStatus?: KitchenOrderStatus;
  statusName: string;
  completedByUserId?: string;
  completedByUserName?: string;
  priority: number;
  /** True when this KitchenOrder contains daily-menu course items (drives per-course flow). */
  isDailyMenuOrder?: boolean;
  /**
   * Name of the daily menu captured at comanda send time (e.g. "Menú Ejecutivo"). Used as
   * the KDS card header for daily-menu cards. Null/empty for historical KOs — the frontend
   * falls back to the generic "Menú del día" label.
   */
  dailyMenuName?: string;
  items: KdsOrderItemDto[];
  kitchenNotes?: string;
}

export interface KitchenOrdersResponse {
  orders: KitchenOrderDto[];
  totalCount: number;
  pendingCount: number;
  preparingCount: number;
  readyCount: number;
}

/**
 * Response wrapper for POST /kitchen/orders. A single comanda may split into multiple
 * KitchenOrders (one for daily-menu courses and one for regular items) when mixed.
 */
export interface CreateKitchenOrdersResponse {
  orders: KitchenOrderDto[];
}

export interface CreateKitchenOrderItemDto {
  orderItemId?: string;
  productName: string;
  quantity: number;
  variants?: string;
  notes?: string;
  seatNumber?: number | null;
}

export interface CreateKitchenOrderDto {
  orderId: string;
  tableNumber: string;
  areaName: string;
  waiterName: string;
  waiterId?: string;
  priority?: number;
  items: CreateKitchenOrderItemDto[];
}

export interface UpdateKitchenOrderStatusDto {
  status: KitchenOrderStatus;
  kitchenNotes?: string;
}

export interface KitchenStatsDto {
  totalOrdersToday: number;
  pendingOrders: number;
  preparingOrders: number;
  readyOrders: number;
  completedOrdersToday: number;
  averageTimeToReadyMinutes: number;
}

// Kitchen Notes Types
export interface KitchenNoteDto {
  id: string;
  message: string;
  isActive: boolean;
  createdAt: string;
  createdByUserName?: string;
}

export interface CreateKitchenNoteDto {
  message: string;
}

export interface UpdateKitchenNoteDto {
  message?: string;
  isActive?: boolean;
}
